import { Dispatch, SetStateAction, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
// @mui
import { Fab, styled, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { trackEvent, usePageView } from 'src/analytics';
import {
  FOOTER_HEIGHT,
  HEADER_HEIGHT,
  MOBILE_MAX_WIDTH,
  SERVICE_CONTROL_PAGE_PATH,
} from 'src/assets/static/constants';
import AppMessageListener from 'src/components/AppMessageListener';
import { LoadingScreen } from 'src/components/loading-screen';
import ScrollToTop from 'src/components/scroll-to-top';
import { useClickTracking } from 'src/hooks/useClickTracking';
import { useStores } from 'src/models';
import { PATH_PAGE } from 'src/routes/paths';

import { Footer } from '../footer';
import { Header } from '../header';
import { HeaderProps } from '../header/Header';

// ----------------------------------------------------------------------

export interface LayoutProps {
  setHeaderProps: Dispatch<SetStateAction<HeaderProps>>;
}

interface MobileLayoutProps {
  needHeader?: boolean;
  needFooter?: boolean;
  needFloating?: boolean;
}

interface FloatingButtonProps {
  needFooter?: boolean;
}

function MobileLayout({
  needHeader = true,
  needFooter = true,
  needFloating = false,
}: MobileLayoutProps) {
  const { authStore } = useStores();
  usePageView();

  const [headerProps, setHeaderProps] = useState<HeaderProps>({});

  return (
    <Container>
      {needHeader && <Header {...headerProps} />}
      <MainContainer needFooter={needFooter}>
        <Outlet context={{ setHeaderProps } as LayoutProps} />
      </MainContainer>
      {authStore?.user && needFooter && <Footer />}
      {needFloating && <FloatingButton needFooter={needFooter} />}
      <ScrollToTop />
      <LoadingScreen />
      <AppMessageListener />
    </Container>
  );
}

const Container = styled('div')({
  width: '100%',
  height: '100vh',
  margin: 'auto',
  maxWidth: MOBILE_MAX_WIDTH,
});

const MainContainer = styled('main', {
  shouldForwardProp: (prop) => prop !== 'needFooter',
})<{ needFooter?: boolean }>(({ needFooter = true }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
  paddingTop: HEADER_HEIGHT,
  paddingBottom: needFooter ? FOOTER_HEIGHT : 0,
}));

export default observer(MobileLayout);

const FloatingButton = ({ needFooter }: FloatingButtonProps) => {
  const navigate = useNavigate();
  const { authStore } = useStores();
  const { withTracking } = useClickTracking();

  return (
    authStore?.user && (
      <Fab
        sx={{
          backgroundColor: '#F44336',
          position: 'fixed',
          bottom: needFooter ? FOOTER_HEIGHT + 20 : 20,
          right: 20,
          '&:hover': {
            backgroundColor: '#D32F2F',
          },
        }}
        onClick={withTracking(SERVICE_CONTROL_PAGE_PATH.craving, () => {
          trackEvent('sos_floating_click');
          navigate(PATH_PAGE.craving.root);
        })}
      >
        <Typography
          sx={{
            color: '#FFFFFF',
            fontWeight: 800,
            fontSize: '18px',
            letterSpacing: '1px',
          }}
        >
          SOS
        </Typography>
      </Fab>
    )
  );
};
